import "./RateMaster.css";
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import editIcon from "../../assets/icons/edit.png";
import BackIcon from "../../assets/icons/arrow.png";
import { useAuthStore } from "../../Services/authStore";
import { toastSuccess, toastError, toastWarning } from "../../Services/toaster";

// ─────────────────────────────────────────────────────────
//  RATE MASTER COMPONENT
// ─────────────────────────────────────────────────────────
export default function RateMaster() {
  const [showForm, setShowForm] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  // const [selectedRate, setSelectedRate] = useState(null);
  const [editingId, setEditingId] = useState(null);

  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Dropdown data from APIs
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [rankOptions, setRankOptions] = useState([]);
  const [loadingDropdowns, setLoadingDropdowns] = useState(false);

  const membershipTypeOptions = [
    { value: "Amature", label: "Amature" },
    { value: "Pro", label: "Pro" },
  ];

  const admissionTypeOptions = [
    { value: "New", label: "New" },
    { value: "Renewal", label: "Renewal" },
  ];

  const [formData, setFormData] = useState({
    category_id: "",
    rate: "",
    is_active: 1,
    from_date: "",
    to_date: "",
    membership_type: "Amature",
    admission_type: "New",
    rank_id: "",
  });
  const [formError, setFormError] = useState("");
  const [formLoading, setFormLoading] = useState(false);

  const token = useAuthStore((state) => state.token);
  const API_URL = process.env.REACT_APP_API_URL;

  const fetchCategories = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/price-category/list/all`, {
        headers: {
          Authorization: `Bearer ${token}`,
          accept: "application/json",
        },
      });

      const data = response.data?.data || response.data || [];
      const mappedCategories = data.map((item) => ({
        id: item.id || item.category_id,
        description: item.description,
        is_active: item.is_active === true || item.is_active === 1,
      }));

      // Filter only active categories
      const activeCategories = mappedCategories.filter(
        (cat) => cat.is_active === true,
      );
      setCategoryOptions(activeCategories);
    } catch (err) {
      console.error("Failed to load categories:", err.response?.data);
      toastError("Failed to load categories");
    }
  }, [API_URL, token]);

  // ─── GET /rank/list/all ───────────────────────────────────
  const fetchRanks = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/rank/list/all`, {
        headers: {
          Authorization: `Bearer ${token}`,
          accept: "application/json",
        },
      });

      const data = response.data?.data || response.data || [];
      const mappedRanks = data.map((item) => ({
        id: item.id || item.rank_id,
        description: item.description,
        is_active: item.is_active === true || item.is_active === 1,
      }));

      // Filter only active ranks
      const activeRanks = mappedRanks.filter((rank) => rank.is_active === true);
      setRankOptions(activeRanks);
    } catch (err) {
      console.error("Failed to load ranks:", err.response?.data);
      toastError("Failed to load ranks");
    }
  }, [API_URL, token]);

  // ─── GET /rate/list/all ───────────────────────────────────
  const getRates = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/rate/list/all`, {
        headers: {
          Authorization: `Bearer ${token}`,
          accept: "application/json",
        },
      });

      // Handle response - map API fields to component fields
      const data = response.data?.data || response.data || [];
      const mappedRates = data.map((item) => ({
        id: item.id || item.rate_id,
        category_id: item.category_id,
        category_description: item.category_name || "",
        rate: item.rate,
        is_active: item.is_active === true || item.is_active === 1,
        from_date: item.from_date,
        to_date: item.to_date,
        membership_type: item.membership_type,
        admission_type: item.admission_type,
        rank_id: item.rank_id,
        rank_description: item.rank_name || "",
      }));

      setRates(mappedRates);
    } catch (err) {
      console.error("Failed to load rates:", err.response?.data);
      toastError("Failed to load rates");
    } finally {
      setLoading(false);
    }
  }, [API_URL, token]);

  // ─── Load all dropdown data ───────────────────────────────────
  const loadDropdowns = useCallback(async () => {
    setLoadingDropdowns(true);
    try {
      await Promise.all([fetchCategories(), fetchRanks()]);
    } catch (err) {
      console.error("Failed to load dropdown data:", err);
    } finally {
      setLoadingDropdowns(false);
    }
  }, [fetchCategories, fetchRanks]);

  useEffect(() => {
    getRates();
    loadDropdowns();
  }, [getRates, loadDropdowns]);

  // ─── GET /rate/{rate_id} ───────────────────────────────────
  const getRateById = useCallback(
    async (id) => {
      try {
        const response = await axios.get(`${API_URL}/rate/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            accept: "application/json",
          },
        });

        const data = response.data?.data || response.data;
        return {
          id: data.id || data.rate_id,
          category_id: data.category_id,
          category_description: data.category_name || "",
          rate: data.rate,
          is_active: data.is_active === true || data.is_active === 1,
          from_date: data.from_date,
          to_date: data.to_date,
          membership_type: data.membership_type,
          admission_type: data.admission_type,
          rank_id: data.rank_id,
          rank_description: data.rank_name || "",
        };
      } catch (err) {
        console.error("Failed to fetch rate:", err.response?.data);
        throw err;
      }
    },
    [API_URL, token],
  );

  // ─── POST /rate/add ───────────────────────────────────
  const createRate = async (rateData) => {
    try {
      const response = await axios.post(
        `${API_URL}/rate/add`,
        {
          category_id: parseInt(rateData.category_id),
          rate: parseFloat(rateData.rate),
          is_active: rateData.is_active === 1,
          from_date: rateData.from_date,
          to_date: rateData.to_date,
          membership_type: rateData.membership_type,
          admission_type: rateData.admission_type,
          rank_id: parseInt(rateData.rank_id),
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            accept: "application/json",
            "Content-Type": "application/json",
          },
        },
      );
      return response.data;
    } catch (err) {
      console.error("Create error:", err.response?.data);
      throw err;
    }
  };

  // ─── PUT /rate/update/{rate_id} ────────────────────────────
  const updateRate = async (id, rateData) => {
    try {
      const response = await axios.put(
        `${API_URL}/rate/update/${id}`,
        {
          category_id: parseInt(rateData.category_id),
          rate: parseFloat(rateData.rate),
          is_active: rateData.is_active === 1,
          from_date: rateData.from_date,
          to_date: rateData.to_date,
          membership_type: rateData.membership_type,
          admission_type: rateData.admission_type,
          rank_id: parseInt(rateData.rank_id),
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            accept: "application/json",
            "Content-Type": "application/json",
          },
        },
      );
      return response.data;
    } catch (err) {
      console.error("Update error:", err.response?.data);
      throw err;
    }
  };

  // ─── Open Edit ───────────────────────────────────────────────────
  const openEdit = async (rate) => {
    setFormError("");
    setIsEditMode(true);
    // setSelectedRate(rate);
    setEditingId(rate.id);
    setFormLoading(true);
    setShowForm(true);

    try {
      const rateData = await getRateById(rate.id);
      setFormData({
        category_id: rateData.category_id || "",
        rate: rateData.rate || "",
        is_active: rateData.is_active ? 1 : 0,
        from_date: rateData.from_date || "",
        to_date: rateData.to_date || "",
        membership_type: rateData.membership_type || "Amature",
        admission_type: rateData.admission_type || "New",
        rank_id: rateData.rank_id || "",
      });
    } catch (err) {
      const msg = "Failed to load rate details.";
      setFormError(msg);
      toastError(msg);
    } finally {
      setFormLoading(false);
    }
  };

  const openAdd = () => {
    setIsEditMode(false);
    // setSelectedRate(null);
    setEditingId(null);
    setFormData({
      category_id: "",
      rate: "",
      is_active: 1,
      from_date: "",
      to_date: "",
      membership_type: "Amature",
      admission_type: "New",
      rank_id: "",
    });
    setFormError("");
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setFormData({
      category_id: "",
      rate: "",
      is_active: 1,
      from_date: "",
      to_date: "",
      membership_type: "Amature",
      admission_type: "New",
      rank_id: "",
    });
    setFormError("");
    // setSelectedRate(null);
    setEditingId(null);
    setIsEditMode(false);
  };

  const handleChange = (e) => {
    const value =
      e.target.type === "checkbox"
        ? e.target.checked
          ? 1
          : 0
        : e.target.value;
    setFormData({ ...formData, [e.target.name]: value });
  };

  // ─── Handle Submit (Create/Update) ───────────────────────────────────
  const handleSubmit = async () => {
    setFormError("");

    // Validation
    if (!formData.category_id) {
      const msg = "Category is required.";
      setFormError(msg);
      toastWarning(msg);
      return;
    }
    if (!formData.rate || formData.rate <= 0) {
      const msg = "Valid rate is required.";
      setFormError(msg);
      toastWarning(msg);
      return;
    }
    if (!formData.from_date) {
      const msg = "From date is required.";
      setFormError(msg);
      toastWarning(msg);
      return;
    }
    if (!formData.to_date) {
      const msg = "To date is required.";
      setFormError(msg);
      toastWarning(msg);
      return;
    }
    if (formData.from_date && formData.to_date < formData.from_date) {
      const msg = "To date cannot be earlier than From date.";
      setFormError(msg);
      toastWarning(msg);
      return;
    }
    if (!formData.rank_id) {
      const msg = "Rank selection is required.";
      setFormError(msg);
      toastWarning(msg);
      return;
    }

    setFormLoading(true);

    try {
      if (isEditMode) {
        // Update existing rate
        await updateRate(editingId, formData);

        // Refresh rates list
        await getRates();

        toastSuccess("Rate updated successfully.");
      } else {
        // Create new rate
        await createRate(formData);

        // Refresh rates list
        await getRates();

        toastSuccess("Rate created successfully.");
      }

      closeForm();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => `${d.loc?.join(".")} — ${d.msg}`).join("\n")
        : detail || "Failed to save rate.";
      setFormError(msg);
      toastError(msg);
    } finally {
      setFormLoading(false);
    }
  };

  // ─── Toggle Rate Status ──────────────────────────────
  const toggleRateStatus = async (rate) => {
    const newStatus = rate.is_active ? 0 : 1;

    try {
      await updateRate(rate.id, {
        category_id: rate.category_id,
        rate: rate.rate,
        is_active: newStatus,
        from_date: rate.from_date,
        to_date: rate.to_date,
        membership_type: rate.membership_type,
        admission_type: rate.admission_type,
        rank_id: rate.rank_id,
      });

      // Update local state
      setRates((prev) =>
        prev.map((r) =>
          r.id === rate.id ? { ...r, is_active: newStatus === 1 } : r,
        ),
      );

      toastSuccess(
        newStatus === 1
          ? "Rate activated successfully."
          : "Rate deactivated successfully.",
      );
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => `${d.loc?.join(".")} — ${d.msg}`).join("\n")
        : detail || "Failed to update rate status.";
      toastError(msg);
    }
  };

  // ── Filter rates ──
  const filteredRates = rates.filter(
    (rate) =>
      rate.category_description
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      (rate.rank_description &&
        rate.rank_description
          .toLowerCase()
          .includes(searchTerm.toLowerCase())) ||
      rate.membership_type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      rate.admission_type?.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  // ── Format currency ──
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  // ── Format date ──
  const formatDate = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // ════════════════════════════════════════════════════
  //  ADD / EDIT FORM
  // ════════════════════════════════════════════════════
  if (showForm) {
    return (
      <div className="rm-rate-form-page">
        {/* Header with Back Arrow */}
        <div className="rm-rate-form-header">
          <div className="rm-rate-form-header-left">
            <button className="rm-rate-back-btn" onClick={closeForm}>
              <img src={BackIcon} alt="Back" className="rm-rate-back-icon" />
            </button>
            <div>
              <h2 className="rm-rate-form-title">
                {isEditMode ? "Edit Rate" : "Add Rate"}
              </h2>
              <p className="rm-rate-form-subtitle">
                {isEditMode
                  ? "Update rate details and status"
                  : "Create a new rate configuration"}
              </p>
            </div>
          </div>
        </div>

        <div className="rm-rate-form-card">
          {formLoading && (
            <p style={{ color: "#9F8E87", fontSize: 13, marginBottom: 14 }}>
              {isEditMode ? "Loading..." : "Saving..."}
            </p>
          )}
          {formError && (
            <p
              style={{
                color: "#b94040",
                fontSize: 13,
                marginBottom: 14,
                whiteSpace: "pre-line",
              }}
            >
              {formError}
            </p>
          )}

          <div className="rm-rate-form-grid">
            {/* Category - from Price Category Master API */}
            <div className="rm-rate-form-group">
              <label>Category *</label>
              <select
                name="category_id"
                value={formData.category_id}
                onChange={handleChange}
                disabled={loadingDropdowns}
              >
                <option value="">
                  {loadingDropdowns
                    ? "Loading categories..."
                    : "Select Category"}
                </option>
                {categoryOptions.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.description}
                  </option>
                ))}
              </select>
              {categoryOptions.length === 0 && !loadingDropdowns && (
                <span style={{ fontSize: 11, color: "#9F8E87", marginTop: 4 }}>
                  No active categories available. Please add categories first.
                </span>
              )}
            </div>

            {/* Rate */}
            <div className="rm-rate-form-group">
              <label>Rate ($) *</label>
              <input
                name="rate"
                type="number"
                step="0.01"
                min="0"
                value={formData.rate}
                onChange={handleChange}
                placeholder="0.00"
              />
            </div>

            {/* From Date */}
            <div className="rm-rate-form-group">
              <label>From Date *</label>
              <input
                name="from_date"
                type="date"
                value={formData.from_date}
                onChange={handleChange}
              />
            </div>

            {/* To Date */}
            <div className="rm-rate-form-group">
              <label>To Date *</label>
              <input
                name="to_date"
                type="date"
                value={formData.to_date}
                min={formData.from_date || undefined}
                onChange={handleChange}
              />
            </div>

            {/* Type of Membership */}
            <div className="rm-rate-form-group">
              <label>Type of Membership *</label>
              <select
                name="membership_type"
                value={formData.membership_type}
                onChange={handleChange}
              >
                {membershipTypeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Admission Type */}
            <div className="rm-rate-form-group">
              <label>Admission Type *</label>
              <select
                name="admission_type"
                value={formData.admission_type}
                onChange={handleChange}
              >
                {admissionTypeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Rank - from Rank Master API */}
            <div className="rm-rate-form-group">
              <label>Rank *</label>
              <select
                name="rank_id"
                value={formData.rank_id}
                onChange={handleChange}
                disabled={loadingDropdowns}
              >
                <option value="">
                  {loadingDropdowns ? "Loading ranks..." : "Select Rank"}
                </option>
                {rankOptions.map((rank) => (
                  <option key={rank.id} value={rank.id}>
                    {rank.description}
                  </option>
                ))}
              </select>
              {rankOptions.length === 0 && !loadingDropdowns && (
                <span style={{ fontSize: 11, color: "#9F8E87", marginTop: 4 }}>
                  No active ranks available. Please add ranks first.
                </span>
              )}
            </div>

            {/* Status — only show in edit mode */}
            {isEditMode && (
              <div className="rm-rate-form-group">
                <label>Status</label>
                <div className="rm-rate-status-toggle">
                  <label className="rm-rate-switch">
                    <input
                      type="checkbox"
                      checked={formData.is_active === 1}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          is_active: e.target.checked ? 1 : 0,
                        })
                      }
                    />
                    <span className="rm-rate-slider"></span>
                  </label>
                  <span className="rm-rate-status-label">
                    {formData.is_active === 1 ? "Active" : "Inactive"}
                  </span>
                </div>
              </div>
            )}
          </div>

          <div className="rm-rate-form-footer">
            <button
              className="rm-rate-btn rm-rate-btn-secondary"
              onClick={closeForm}
            >
              Cancel
            </button>
            <button
              className="rm-rate-btn rm-rate-btn-primary"
              onClick={handleSubmit}
              disabled={formLoading || loadingDropdowns}
            >
              {formLoading
                ? "Saving..."
                : isEditMode
                  ? "Update Rate"
                  : "Save Rate"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ════════════════════════════════════════════════════
  //  RATE LIST
  // ════════════════════════════════════════════════════
  return (
    <div className="rm-rate-list-page">
      {/* ─── Top Bar ─── */}
      <div className="rm-rate-page-header">
        <div>
          <h1 className="rm-rate-page-title">Rate Master</h1>
          <p className="rm-rate-page-subtitle">
            Manage rates and pricing configurations
          </p>
        </div>
        <div className="rm-rate-page-actions">
          <div className="rm-rate-search-box">
            <span className="rm-rate-search-icon">🔍</span>
            <input
              type="text"
              placeholder="Search rates..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <button className="rm-rate-btn rm-rate-btn-primary" onClick={openAdd}>
            + Add Rate
          </button>
        </div>
      </div>

      {/* ─── Table ─── */}
      <div className="rm-rate-table-card">
        <div className="rm-rate-table-responsive">
          <table className="rm-rate-table">
            <thead>
              <tr>
                <th className="rm-rate-col-id">#</th>
                <th className="rm-rate-col-category">Category</th>
                <th className="rm-rate-col-rate">Rate</th>
                <th className="rm-rate-col-dates">Date Range</th>
                <th className="rm-rate-col-membership">Membership</th>
                <th className="rm-rate-col-admission">Admission</th>
                <th className="rm-rate-col-rank">Rank</th>
                <th className="rm-rate-col-active">Status</th>
                <th className="rm-rate-col-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="9" className="rm-rate-empty-row">
                    Loading rates...
                  </td>
                </tr>
              ) : filteredRates.length === 0 ? (
                <tr>
                  <td colSpan="9">
                    <div className="rm-rate-empty-state">
                      <div className="rm-rate-empty-icon">💰</div>
                      <div className="rm-rate-empty-title">No Rates Found</div>
                      <div className="rm-rate-empty-desc">
                        {searchTerm
                          ? `No rates match "${searchTerm}"`
                          : "Start by adding your first rate"}
                      </div>
                      {/* {!searchTerm && (
                        <button
                          className="rm-rate-btn rm-rate-btn-primary"
                          onClick={openAdd}
                        >
                          + Add Rate
                        </button>
                      )} */}
                    </div>
                  </td>
                </tr>
              ) : (
                filteredRates.map((rate, index) => (
                  <tr key={rate.id}>
                    <td className="rm-rate-col-id">{index + 1}</td>
                    <td className="rm-rate-col-category">
                      {rate.category_description || "-"}
                    </td>
                    <td className="rm-rate-col-rate">
                      {formatCurrency(rate.rate)}
                    </td>
                    <td className="rm-rate-col-dates">
                      <div className="rm-rate-date-range">
                        <span>{formatDate(rate.from_date)}</span>
                        <span className="rm-rate-date-separator">→</span>
                        <span>{formatDate(rate.to_date)}</span>
                      </div>
                    </td>
                    <td className="rm-rate-col-membership">
                      <span
                        className={`rm-rate-membership-badge rm-rate-membership-${rate.membership_type?.toLowerCase()}`}
                      >
                        {rate.membership_type}
                      </span>
                    </td>
                    <td className="rm-rate-col-admission">
                      <span
                        className={`rm-rate-admission-badge rm-rate-admission-${rate.admission_type?.toLowerCase()}`}
                      >
                        {rate.admission_type}
                      </span>
                    </td>
                    <td className="rm-rate-col-rank">
                      {rate.rank_description || "-"}
                    </td>
                    <td className="rm-rate-col-active">
                      <label className="rm-rate-switch">
                        <input
                          type="checkbox"
                          checked={rate.is_active}
                          onChange={() => toggleRateStatus(rate)}
                        />
                        <span className="rm-rate-slider"></span>
                      </label>
                    </td>
                    <td className="rm-rate-col-actions">
                      <div className="rm-rate-action-group">
                        <button
                          className="rm-rate-action-btn rm-rate-action-btn-edit"
                          onClick={() => openEdit(rate)}
                          title="Edit"
                        >
                          <img
                            src={editIcon}
                            alt="Edit"
                            className="rm-rate-action-icon"
                          />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
